// stockService.js - Centralized service for managing stock operations

const stockService = {
  _loadProducts() {
    try {
      const productsData = localStorage.getItem('products') || localStorage.getItem('stockpro_products');
      return productsData ? JSON.parse(productsData) : [];
    } catch (error) {
      console.error('Error loading products:', error);
      return [];
    }
  },

  _saveProducts(products) {
    try {
      localStorage.setItem('products', JSON.stringify(products));
      localStorage.setItem('stockpro_products', JSON.stringify(products));
      return true;
    } catch (error) {
      console.error('Error saving products:', error);
      return false;
    }
  },

  _loadStockMovements() {
    try {
      const stockData = localStorage.getItem('stockpro_stock');
      return stockData ? JSON.parse(stockData) : [];
    } catch (error) {
      console.error('Error loading stock movements:', error);
      return [];
    }
  },

  _saveStockMovements(movements) {
    try {
      localStorage.setItem('stockpro_stock', JSON.stringify(movements));
      return true;
    } catch (error) {
      console.error('Error saving stock movements:', error);
      return false;
    }
  },

  isProductInWarehouse(productId, warehouseId) {
    const productIdStr = String(productId);
    const warehouseIdStr = String(warehouseId);
    
    const products = this._loadProducts();
    const product = products.find(p => String(p.id) === productIdStr);
    
    if (!product) return false;
    
    if (product.warehouseIds && Array.isArray(product.warehouseIds)) {
      const hasWarehouse = product.warehouseIds.some(wId => String(wId) === warehouseIdStr);
      if (hasWarehouse) return true;
    }
    
    if (product.warehouseStock && typeof product.warehouseStock === 'object') {
      if (product.warehouseStock[warehouseIdStr] !== undefined && 
          parseInt(product.warehouseStock[warehouseIdStr]) > 0) {
        return true;
      }
      
      const warehouseIdNum = parseInt(warehouseIdStr);
      if (!isNaN(warehouseIdNum) && 
          product.warehouseStock[warehouseIdNum] !== undefined && 
          parseInt(product.warehouseStock[warehouseIdNum]) > 0) {
        return true;
      }
    }
    
    return false;
  },

  getProductStockByWarehouse(productId, warehouseId) {
    const productIdStr = String(productId);
    const warehouseIdStr = String(warehouseId);
    
    const products = this._loadProducts();
    const product = products.find(p => String(p.id) === productIdStr);
    
    if (!product) return 0;
    
    if (product.warehouseStock && typeof product.warehouseStock === 'object') {
      if (product.warehouseStock[warehouseIdStr] !== undefined) {
        return parseInt(product.warehouseStock[warehouseIdStr]) || 0;
      }
      
      const warehouseIdNum = parseInt(warehouseIdStr);
      if (!isNaN(warehouseIdNum) && product.warehouseStock[warehouseIdNum] !== undefined) {
        return parseInt(product.warehouseStock[warehouseIdNum]) || 0;
      }
    }
    
    return 0;
  },

  async updateProductStock(productId, movementData) {
    try {
      const products = this._loadProducts();
      const productIndex = products.findIndex(p => p.id === productId);
      
      if (productIndex === -1) {
        throw new Error(`Product with ID ${productId} not found`);
      }
      
      const product = products[productIndex];
      const updatedProduct = { ...product };
      
      const type = movementData.type;
      const quantityAsNumber = parseInt(movementData.quantity) || 0;
      const warehouse = String(movementData.warehouse || movementData.location);
      
      if (!updatedProduct.warehouseIds) {
        updatedProduct.warehouseIds = [];
      }
      
      updatedProduct.warehouseIds = updatedProduct.warehouseIds.map(id => String(id));
      
      if (!updatedProduct.warehouseIds.includes(String(warehouse))) {
        updatedProduct.warehouseIds.push(String(warehouse));
      }
      
      if (!updatedProduct.warehouseStock) {
        updatedProduct.warehouseStock = {};
      }
      
      if (updatedProduct.warehouseStock[parseInt(warehouse)] !== undefined) {
        delete updatedProduct.warehouseStock[parseInt(warehouse)];
      }
      
      const currentWarehouseStock = parseInt(updatedProduct.warehouseStock[String(warehouse)] || 0);
      let newWarehouseStock = currentWarehouseStock;
      
      if (type === 'entrée') {
        newWarehouseStock = currentWarehouseStock + quantityAsNumber;
      } else if (type === 'sortie') {
        newWarehouseStock = Math.max(0, currentWarehouseStock - quantityAsNumber);
      } else if (type === 'ajustement') {
        newWarehouseStock = quantityAsNumber;
      }
      
      newWarehouseStock = Math.max(0, newWarehouseStock);
      
      updatedProduct.warehouseStock[String(warehouse)] = newWarehouseStock;
      
      let totalStock = 0;
      for (const warehouseId in updatedProduct.warehouseStock) {
        totalStock += parseInt(updatedProduct.warehouseStock[warehouseId] || 0);
      }
      
      updatedProduct.stock = totalStock;
      
      products[productIndex] = updatedProduct;
      this._saveProducts(products);
      
      if (!movementData.skipMovementRecord && !movementData.isTransfer) {
        const stockMovements = this._loadStockMovements();
        
        const movementRecord = {
          id: `MOV-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          type,
          productId,
          productName: updatedProduct.name,
          quantity: quantityAsNumber,
          warehouse: String(warehouse),
          location: String(warehouse),
          notes: movementData.notes || '',
          reference: movementData.reference || '',
          createdAt: new Date().toISOString()
        };
        
        stockMovements.unshift(movementRecord);
        this._saveStockMovements(stockMovements);
      }
      
      return updatedProduct;
    } catch (error) {
      console.error(`Error updating stock for product ${productId}:`, error);
      throw error;
    }
  },

  async transferStock(transferData) {
    const { sourceWarehouseId, destinationWarehouseId, items, date, notes } = transferData;
    
    const sourceWarehouseIdStr = String(sourceWarehouseId);
    const destWarehouseIdStr = String(destinationWarehouseId);
    
    const transferId = `TRF-${Date.now()}`;
    
    const result = {
      success: false,
      transferId,
      sourceWarehouseId: sourceWarehouseIdStr,
      destinationWarehouseId: destWarehouseIdStr,
      date: date || new Date().toISOString().split('T')[0],
      processedItems: [],
      failedItems: [],
      error: null,
      verificationResults: [] // Add verification results
    };
    
    try {
      const products = this._loadProducts();
      const originalProducts = JSON.parse(JSON.stringify(products)); // Deep copy for verification
      
      for (const item of items) {
        const productId = String(item.id); // Ensure product ID is a string
        const quantity = parseInt(item.quantity) || 0;
        
        try {
          const productIndex = products.findIndex(p => String(p.id) === productId);
          if (productIndex === -1) {
            throw new Error(`Product with ID ${productId} not found`);
          }
          
          const product = products[productIndex];
          const updatedProduct = { ...product };
          
          if (!updatedProduct.warehouseIds) {
            updatedProduct.warehouseIds = [];
          }
          
          updatedProduct.warehouseIds = updatedProduct.warehouseIds.map(id => String(id));
          
          if (!updatedProduct.warehouseIds.includes(sourceWarehouseIdStr)) {
            updatedProduct.warehouseIds.push(sourceWarehouseIdStr);
          }
          if (!updatedProduct.warehouseIds.includes(destWarehouseIdStr)) {
            updatedProduct.warehouseIds.push(destWarehouseIdStr);
          }
          
          if (!updatedProduct.warehouseStock) {
            updatedProduct.warehouseStock = {};
          }
          
          const currentSourceStock = parseInt(updatedProduct.warehouseStock[sourceWarehouseIdStr] || 0);
          const currentDestStock = parseInt(updatedProduct.warehouseStock[destWarehouseIdStr] || 0);
          
          if (quantity <= 0) {
            throw new Error(`Invalid quantity: ${quantity}`);
          }
          
          if (currentSourceStock < quantity) {
            throw new Error(`Insufficient stock in source warehouse: ${currentSourceStock} < ${quantity}`);
          }
          
          const newSourceStock = currentSourceStock - quantity;
          const newDestStock = currentDestStock + quantity;
          
          // Verification step: Ensure source stock is properly calculated
          console.log(`TRANSFER VERIFICATION: Product ${productId} (${product.name})`);
          console.log(`  Source warehouse ${sourceWarehouseIdStr}: ${currentSourceStock} - ${quantity} = ${newSourceStock}`);
          console.log(`  Destination warehouse ${destWarehouseIdStr}: ${currentDestStock} + ${quantity} = ${newDestStock}`);
          
          // Apply the changes with debugging
          console.log(`TRANSFER DEBUG: Before update - product ${productId} warehouse stock:`, JSON.stringify(updatedProduct.warehouseStock));
          console.log(`TRANSFER DEBUG: Setting source warehouse ${sourceWarehouseIdStr} stock to ${newSourceStock}`);
          console.log(`TRANSFER DEBUG: Setting destination warehouse ${destWarehouseIdStr} stock to ${newDestStock}`);
          
          // First ensure both warehouses are in the warehouseIds array
          if (!updatedProduct.warehouseIds.includes(sourceWarehouseIdStr)) {
            updatedProduct.warehouseIds.push(sourceWarehouseIdStr);
          }
          if (!updatedProduct.warehouseIds.includes(destWarehouseIdStr)) {
            updatedProduct.warehouseIds.push(destWarehouseIdStr);
          }
          
          // Set the stock values as numbers (not strings) and ensure they're set correctly
          updatedProduct.warehouseStock[sourceWarehouseIdStr] = parseInt(newSourceStock);
          updatedProduct.warehouseStock[destWarehouseIdStr] = parseInt(newDestStock);
          
          console.log(`TRANSFER DEBUG: After update - product ${productId} warehouse stock:`, JSON.stringify(updatedProduct.warehouseStock));
          console.log(`TRANSFER DEBUG: After update - warehouseIds:`, updatedProduct.warehouseIds);
          
          let totalStock = 0;
          for (const warehouseId in updatedProduct.warehouseStock) {
            totalStock += parseInt(updatedProduct.warehouseStock[warehouseId] || 0);
          }
          
          updatedProduct.stock = totalStock;
          
          products[productIndex] = updatedProduct;
          
          result.processedItems.push({
            id: productId,
            name: product.name,
            quantity,
            sourceStock: newSourceStock,
            destinationStock: newDestStock
          });
        } catch (itemError) {
          result.failedItems.push({
            id: productId,
            quantity,
            error: itemError.message
          });
        }
      }
      
      if (result.processedItems.length > 0) {
        // ===== CRITICAL FIX: Ensure data consistency =====
        
        // 1. Save the updated products with multiple backup attempts
        this._saveProducts(products);
        
        // 2. Double-check all products are saved with correct values by reading them back
        let verifiedProducts = this._loadProducts();
        
        // 3. Force direct verification and correction for each transferred product
        for (const item of result.processedItems) {
          const productId = item.id;
          let verifiedProduct = verifiedProducts.find(p => String(p.id) === String(productId));
          
          if (verifiedProduct) {
            // Read current values
            let sourceStock = parseInt(verifiedProduct.warehouseStock?.[sourceWarehouseIdStr] || 0);
            let destStock = parseInt(verifiedProduct.warehouseStock?.[destWarehouseIdStr] || 0);
            
            console.log(`TRANSFER CHECK for ${verifiedProduct.name}:`);
            console.log(`  Source warehouse: Expected=${item.sourceStock}, Found=${sourceStock}`);
            console.log(`  Destination warehouse: Expected=${item.destinationStock}, Found=${destStock}`);
            
            // Always create a fixed version to ensure consistency
            const fixedProduct = { ...verifiedProduct };
            
            // Ensure required properties exist
            if (!fixedProduct.warehouseStock) fixedProduct.warehouseStock = {};
            if (!fixedProduct.warehouseIds) fixedProduct.warehouseIds = [];
            
            // Ensure warehouseIds are strings and include both warehouses
            fixedProduct.warehouseIds = fixedProduct.warehouseIds.map(id => String(id));
            
            // Ensure both warehouses are in the warehouseIds array (critical for showing products in warehouses)
            if (!fixedProduct.warehouseIds.includes(sourceWarehouseIdStr)) {
              console.log(`Adding source warehouse ${sourceWarehouseIdStr} to warehouseIds`);
              fixedProduct.warehouseIds.push(sourceWarehouseIdStr);
            }
            
            if (!fixedProduct.warehouseIds.includes(destWarehouseIdStr)) {
              console.log(`Adding destination warehouse ${destWarehouseIdStr} to warehouseIds`);
              fixedProduct.warehouseIds.push(destWarehouseIdStr);
            }
            
            // ALWAYS force correct values regardless of verification
            console.log(`FORCING CORRECT VALUES for ${verifiedProduct.name}`);
            console.log(`Before fix - warehouseStock:`, JSON.stringify(fixedProduct.warehouseStock));
            
            // Set stock values directly as numbers (not strings)
            fixedProduct.warehouseStock[sourceWarehouseIdStr] = parseInt(item.sourceStock);
            fixedProduct.warehouseStock[destWarehouseIdStr] = parseInt(item.destinationStock);
            
            console.log(`After fix - warehouseStock:`, JSON.stringify(fixedProduct.warehouseStock));
            
            // Update total stock
            let totalStock = 0;
            for (const warehouseId in fixedProduct.warehouseStock) {
              totalStock += parseInt(fixedProduct.warehouseStock[warehouseId] || 0);
            }
            fixedProduct.stock = totalStock;
            
            // Update the products array with fixed values
            const fixedProducts = verifiedProducts.map(p => 
              String(p.id) === String(productId) ? fixedProduct : p
            );
            
            // Save the fixed products back to storage
            this._saveProducts(fixedProducts);
            
            // Update verification array for next iteration
            verifiedProducts = fixedProducts;
            
            // Log the correction
            console.log(`TRANSFER UPDATE: Product ${productId} (${fixedProduct.name})`);
            console.log(`  Source warehouse ${sourceWarehouseIdStr}: Now=${fixedProduct.warehouseStock[sourceWarehouseIdStr]}`);
            console.log(`  Destination warehouse ${destWarehouseIdStr}: Now=${fixedProduct.warehouseStock[destWarehouseIdStr]}`);
            console.log(`  Total stock: ${totalStock}`);
            
            // Store the verification results
            result.verificationResults.push({
              productId,
              productName: verifiedProduct.name,
              sourceWarehouse: sourceWarehouseIdStr,
              sourceStock: fixedProduct.warehouseStock[sourceWarehouseIdStr],
              destWarehouse: destWarehouseIdStr,
              destStock: fixedProduct.warehouseStock[destWarehouseIdStr],
              fixed: true
            });
          }
        }
        
        // 4. Force update both localStorage keys for consistency
        const finalProducts = this._loadProducts();
        localStorage.setItem('stockpro_products', JSON.stringify(finalProducts));
        localStorage.setItem('products', JSON.stringify(finalProducts));
        
        const stockMovements = this._loadStockMovements();
        
        // Create a proper transfer movement record that works with both source and destination warehouses
        const transferMovement = {
          // Basic movement info
          id: transferId,
          type: 'transfert',
          date: date || new Date().toISOString().split('T')[0],
          createdAt: new Date().toISOString(),
          reference: `Transfer-${transferId}`,
          
          // Critical warehouse identifiers - BOTH source and destination must be properly set
          sourceWarehouse: sourceWarehouseIdStr,  // For source warehouse
          destinationWarehouse: destWarehouseIdStr,  // For destination warehouse
          warehouse: destWarehouseIdStr,  // Legacy support
          location: destWarehouseIdStr,  // Legacy support
          
          // Details about the transfer
          items: result.processedItems.map(item => ({
            id: item.id,
            name: item.name,
            quantity: item.quantity,
            sourceStock: item.sourceStock,  // Include current stock levels
            destinationStock: item.destinationStock  // Include current stock levels
          })),
          quantity: result.processedItems.reduce((sum, item) => sum + item.quantity, 0),
          notes: notes || 'Transfert entre entrepôts',
          
          // Flag this as a transfer to prevent double-counting
          isTransfer: true
        };
        
        stockMovements.unshift(transferMovement);
        this._saveStockMovements(stockMovements);
        
        result.success = true;
        
        // Get the final updated products
        const updatedProducts = this._loadProducts();
        
        // CRITICAL: Create multiple sequential events to ensure ALL components update
        // First delay a bit to ensure storage is fully saved
        setTimeout(() => {
          console.log('DISPATCHING TRANSFER EVENTS...');
          
          // 1. Notify about specific products being updated
          for (const item of transferMovement.items) {
            // Get the latest product data
            const updatedProduct = updatedProducts.find(p => p.id === item.id);
            
            // Create product-specific event
            const productEvent = new CustomEvent('product-stock-updated', {
              detail: {
                productId: item.id,
                productName: item.name,
                sourceWarehouse: sourceWarehouseIdStr,
                destinationWarehouse: destWarehouseIdStr,
                quantity: item.quantity,
                timestamp: new Date().toISOString(),
                product: updatedProduct // Include full product data
              }
            });
            
            // Dispatch product-specific event
            console.log(`Dispatching update event for product ${item.name}`);
            window.dispatchEvent(productEvent);
          }
          
          // 2. Notify source warehouse specifically
          const sourceEvent = new CustomEvent('warehouse-inventory-updated', {
            detail: {
              warehouseId: sourceWarehouseIdStr,
              type: 'transfer-source',
              items: transferMovement.items,
              timestamp: new Date().toISOString()
            }
          });
          console.log(`Dispatching event for source warehouse ${sourceWarehouseIdStr}`);
          window.dispatchEvent(sourceEvent);
          
          // 3. Notify destination warehouse specifically
          const destEvent = new CustomEvent('warehouse-inventory-updated', {
            detail: {
              warehouseId: destWarehouseIdStr,
              type: 'transfer-destination',
              items: transferMovement.items,
              timestamp: new Date().toISOString()
            }
          });
          console.log(`Dispatching event for destination warehouse ${destWarehouseIdStr}`);
          window.dispatchEvent(destEvent);
          
          // 4. Main transfer event with complete data
          const transferEvent = new CustomEvent('transfer-completed', {
            detail: { 
              transfer: transferMovement,
              sourceWarehouse: sourceWarehouseIdStr,
              destinationWarehouse: destWarehouseIdStr,
              items: transferMovement.items,
              timestamp: new Date().toISOString(),
              products: updatedProducts
            }
          });
          console.log('Dispatching main transfer-completed event');
          window.dispatchEvent(transferEvent);
          
          // 5. Global refresh as final backup
          setTimeout(() => {
            console.log('Dispatching final global refresh event');
            window.dispatchEvent(new CustomEvent('refresh-data'));
          }, 200);
        }, 100);
      } else {
        result.error = 'No items were processed successfully';
      }
    } catch (error) {
      result.error = error.message;
    }
    
    return result;
  },

  async addStockEntry(entryData) {
    try {
      const { id, date, supplierId, reference, documentType, warehouse, notes, items, totalValue } = entryData;
      
      const entryId = id || `ENT-${Date.now()}`;
      
      const stockMovement = {
        id: entryId,
        date: date || new Date().toISOString().split('T')[0],
        type: 'entrée',
        supplierId,
        reference,
        documentType,
        warehouse: String(warehouse),
        location: String(warehouse),
        notes,
        items: items.map(item => ({
          id: item.productId,
          name: item.productName,
          quantity: parseInt(item.quantity) || 0,
          unitPrice: item.unitPrice
        })),
        totalValue,
        createdAt: new Date().toISOString()
      };
      
      const processedItems = [];
      const failedItems = [];
      
      for (const item of items) {
        try {
          await this.updateProductStock(item.productId, {
            type: 'entrée',
            quantity: parseInt(item.quantity) || 0,
            warehouse: String(warehouse),
            reference,
            notes,
            skipMovementRecord: true
          });
          
          processedItems.push({
            id: item.productId,
            name: item.productName,
            quantity: item.quantity
          });
        } catch (itemError) {
          failedItems.push({
            id: item.productId,
            quantity: item.quantity,
            error: itemError.message
          });
        }
      }
      
      const stockMovements = this._loadStockMovements();
      stockMovements.unshift(stockMovement);
      this._saveStockMovements(stockMovements);
      
      return {
        success: processedItems.length > 0,
        entryId,
        processedItems,
        failedItems,
        error: failedItems.length > 0 ? `${failedItems.length} items failed` : null
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async addStockExit(exitData) {
    try {
      const { id, date, customerId, employeeId, reference, documentType, warehouse, notes, items, totalValue } = exitData;
      
      const exitId = id || `SOR-${Date.now()}`;
      
      const stockMovement = {
        id: exitId,
        date: date || new Date().toISOString().split('T')[0],
        type: 'sortie',
        customerId,
        employeeId,
        reference,
        documentType,
        warehouse: String(warehouse),
        location: String(warehouse),
        notes,
        items: items.map(item => ({
          id: item.productId,
          name: item.productName,
          quantity: parseInt(item.quantity) || 0,
          unitPrice: item.unitPrice
        })),
        totalValue,
        createdAt: new Date().toISOString()
      };
      
      const processedItems = [];
      const failedItems = [];
      
      for (const item of items) {
        try {
          await this.updateProductStock(item.productId, {
            type: 'sortie',
            quantity: parseInt(item.quantity) || 0,
            warehouse: String(warehouse),
            reference,
            notes,
            skipMovementRecord: true
          });
          
          processedItems.push({
            id: item.productId,
            name: item.productName,
            quantity: item.quantity
          });
        } catch (itemError) {
          failedItems.push({
            id: item.productId,
            quantity: item.quantity,
            error: itemError.message
          });
        }
      }
      
      const stockMovements = this._loadStockMovements();
      stockMovements.unshift(stockMovement);
      this._saveStockMovements(stockMovements);
      
      return {
        success: processedItems.length > 0,
        exitId,
        processedItems,
        failedItems,
        error: failedItems.length > 0 ? `${failedItems.length} items failed` : null
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  getStockMovements() {
    return this._loadStockMovements();
  },

  async addStockMovement(movement) {
    try {
      const stockMovements = this._loadStockMovements();
      
      if (!movement.id) {
        const prefix = movement.type === 'entrée' ? 'ENT' : 
                      movement.type === 'sortie' ? 'SOR' : 
                      movement.type === 'transfert' ? 'TRF' : 'MOV';
        movement.id = `${prefix}-${Date.now()}`;
      }
      
      if (!movement.createdAt) {
        movement.createdAt = new Date().toISOString();
      }
      
      stockMovements.unshift(movement);
      this._saveStockMovements(stockMovements);
      
      return movement;
    } catch (error) {
      console.error('Error adding stock movement:', error);
      throw error;
    }
  },

  getStockAlerts() {
    try {
      const products = this._loadProducts();
      const lowStockProducts = products.filter(p => {
        const stock = parseInt(p.stock) || 0;
        const threshold = parseInt(p.minStockLevel || p.lowStockThreshold) || 5;
        return stock <= threshold;
      });
      
      return lowStockProducts.map(p => ({
        id: p.id,
        name: p.name,
        stock: parseInt(p.stock) || 0,
        threshold: parseInt(p.minStockLevel || p.lowStockThreshold) || 5
      }));
    } catch (error) {
      return [];
    }
  },

  getStockValuation() {
    try {
      const products = this._loadProducts();
      
      let totalItems = 0;
      let totalValue = 0;
      let warehouseValues = {};
      
      products.forEach(product => {
        const stock = parseInt(product.stock) || 0;
        const price = parseFloat(product.price || product.cost) || 0;
        const value = stock * price;
        
        totalItems += stock;
        totalValue += value;
        
        if (product.warehouseStock && typeof product.warehouseStock === 'object') {
          for (const warehouseId in product.warehouseStock) {
            const warehouseStock = parseInt(product.warehouseStock[warehouseId]) || 0;
            const warehouseValue = warehouseStock * price;
            
            if (!warehouseValues[warehouseId]) {
              warehouseValues[warehouseId] = {
                items: 0,
                value: 0
              };
            }
            
            warehouseValues[warehouseId].items += warehouseStock;
            warehouseValues[warehouseId].value += warehouseValue;
          }
        }
      });
      
      return {
        totalItems,
        totalValue,
        warehouseValues,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        totalItems: 0,
        totalValue: 0,
        warehouseValues: {},
        timestamp: new Date().toISOString(),
        error: error.message
      };
    }
  },
  
  // Verify product data integrity
  verifyProductIntegrity(productId) {
    try {
      const products = this._loadProducts();
      const product = products.find(p => String(p.id) === String(productId));
      
      if (!product) {
        return {
          valid: false,
          issues: [`Product with ID ${productId} not found`]
        };
      }
      
      const issues = [];
      
      // Check if warehouseIds and warehouseStock are initialized
      if (!product.warehouseIds) {
        issues.push('warehouseIds array is missing');
      }
      
      if (!product.warehouseStock) {
        issues.push('warehouseStock object is missing');
      }
      
      // If no issues found so far, proceed with deeper checks
      if (issues.length === 0) {
        // Check if all warehouseIds are strings
        const nonStringIds = product.warehouseIds.filter(id => typeof id !== 'string');
        if (nonStringIds.length > 0) {
          issues.push(`Found ${nonStringIds.length} non-string warehouse IDs`);
        }
        
        // Check if all warehouseIds have corresponding warehouseStock entries
        const missingStockEntries = product.warehouseIds.filter(id => 
          product.warehouseStock[id] === undefined
        );
        if (missingStockEntries.length > 0) {
          issues.push(`Found ${missingStockEntries.length} warehouse IDs without stock entries`);
        }
        
        // Check if all warehouseStock entries have corresponding warehouseIds
        const extraStockEntries = Object.keys(product.warehouseStock).filter(id => 
          !product.warehouseIds.includes(id)
        );
        if (extraStockEntries.length > 0) {
          issues.push(`Found ${extraStockEntries.length} stock entries without warehouse IDs`);
        }
        
        // Check if total stock matches sum of warehouse stocks
        const calculatedStock = Object.values(product.warehouseStock).reduce(
          (sum, stock) => sum + parseInt(stock || 0), 
          0
        );
        
        if (parseInt(product.stock || 0) !== calculatedStock) {
          issues.push(`Total stock (${product.stock}) doesn't match sum of warehouse stocks (${calculatedStock})`);
        }
      }
      
      return {
        valid: issues.length === 0,
        issues
      };
    } catch (error) {
      console.error(`Error verifying product integrity for ${productId}:`, error);
      return {
        valid: false,
        issues: [`Error during verification: ${error.message}`]
      };
    }
  }
};

export default stockService;
